import React, { useEffect, useRef, useState } from 'react';
import { REACTION_OPTIONS } from './reactionEmojiMap';
import { DEFAULT_QUICK_REACT_TOKENS } from './quickReactTokens';

/**
 * MessageHoverToolbar — Slack-style hover/focus toolbar for a single
 * message. KAP-309 + KAP-311 + KAP-313.
 *
 * Pure presentational + state-light: parent owns the picker, the edit
 * trigger, the thread opener, the delete/forward/pin/bookmark mutations,
 * and the add-reaction callback. This component just lays out the chips
 * and handles its own kebab-menu open/close + Esc semantics.
 *
 * Quick-react chips fire `onQuickReact(token)` directly (no picker
 * roundtrip). Tokens are server-canonical (e.g. `check`, `eyes`,
 * `raised_hands`); the displayed glyph is the curated emoji from
 * `reactionEmojiMap`. KAP-310: the trio is supplied by the parent
 * (from `getMyProfile.quickReactChips` with a default fallback) so the
 * presentational layer stays preference-agnostic.
 */

function getQuickReactChips(tokens) {
  const lookup = new Map(REACTION_OPTIONS.map((opt) => [opt.token, opt]));
  return tokens
    .map((t) => lookup.get(t))
    .filter(Boolean);
}

function MessageHoverToolbar({
  quickReactTokens = DEFAULT_QUICK_REACT_TOKENS,
  canEdit = false,
  // KAP-313 — sender OR admin sees Delete in the kebab. Parent passes
  // both `canDelete` and `onDelete`; the menu item is hidden unless both
  // are truthy.
  canDelete = false,
  // KAP-311.1 — sender or any member can forward; hidden on deleted rows.
  canForward = false,
  // KAP-311.2 — any conversation member can pin; banner driven elsewhere.
  canPin = false,
  isPinned = false,
  // KAP-311.3 — per-user bookmark; isBookmarked drives the menu label.
  canBookmark = false,
  isBookmarked = false,
  // Copy-message-text is purely client-side (writeText to clipboard); the
  // raw message content is passed in by the parent. Hidden on deleted rows
  // because the bubble swaps content for a placeholder.
  messageText = '',
  onQuickReact,
  onOpenPicker,
  onOpenThread,
  onEdit,
  onDelete,
  onCopyLink,
  onForward,
  onPin,
  onUnpin,
  onBookmark,
  onUnbookmark,
  onDismiss
}) {
  const [kebabOpen, setKebabOpen] = useState(false);
  // Whether the dropdown should flip upward to avoid clipping at the
  // bottom of the viewport. Measured at the moment the kebab opens
  // (browser-only — `null` is the safe default for SSR / first paint).
  const [menuDirection, setMenuDirection] = useState('down');
  // The menu is rendered with `position: fixed` so it escapes the
  // `msg-list-container`'s overflow:hidden (which would otherwise clip
  // the lower items). `menuStyle` carries the computed top + right and
  // a visibility flag — we hide the menu for one paint to measure its
  // natural height, then position + reveal in a single frame.
  const [menuStyle, setMenuStyle] = useState({ visibility: 'hidden' });
  const [messageCopiedAt, setMessageCopiedAt] = useState(0);
  const toolbarRef = useRef(null);
  const kebabRef = useRef(null);
  const menuRef = useRef(null);

  const chips = getQuickReactChips(quickReactTokens);

  // Close the kebab on outside click and on Esc. Esc on the toolbar
  // itself bubbles up to onDismiss so the parent can return focus to
  // the message wrapper.
  useEffect(() => {
    if (!kebabOpen) return undefined;
    const handlePointerDown = (event) => {
      if (
        kebabRef.current && !kebabRef.current.contains(event.target) &&
        menuRef.current && !menuRef.current.contains(event.target)
      ) {
        setKebabOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [kebabOpen]);

  // Slack-parity: when the kebab opens, position the dropdown so it
  // never falls off the viewport AND escapes the parent message-list's
  // overflow:hidden (which would otherwise visually clip lower items).
  //
  // Approach: render the menu position:fixed (CSS), measure its natural
  // height once mounted, then choose `down` or `up` based on viewport
  // space + write computed top/right via inline style. Position is
  // anchored to the kebab trigger so the menu visually feels attached.
  // Reposition on scroll/resize while open.
  useEffect(() => {
    if (!kebabOpen || !menuRef.current || !kebabRef.current) {
      setMenuStyle({ visibility: 'hidden' });
      return undefined;
    }

    const positionMenu = () => {
      if (!menuRef.current || !kebabRef.current) return;
      const menuRect = menuRef.current.getBoundingClientRect();
      const triggerRect = kebabRef.current.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      const margin = 8;
      const spaceBelow = vh - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const fitsBelow = spaceBelow >= menuRect.height + margin;
      const fitsAbove = spaceAbove >= menuRect.height + margin;
      const direction = !fitsBelow && (fitsAbove || spaceAbove > spaceBelow) ? 'up' : 'down';
      const top = direction === 'down'
        ? Math.max(margin, triggerRect.bottom + 4)
        : Math.max(margin, triggerRect.top - menuRect.height - 4);
      // Anchor the menu's right edge to the trigger's right edge so it
      // visually emanates from the kebab. Clamp at the viewport edges.
      const right = Math.max(margin, Math.min(vw - triggerRect.right, vw - menuRect.width - margin));
      setMenuDirection(direction);
      setMenuStyle({
        position: 'fixed',
        top: `${Math.round(top)}px`,
        right: `${Math.round(right)}px`,
        visibility: 'visible'
      });
    };

    // First paint: measure the natural height (the menu is mounted but
    // hidden), then place it on the next frame.
    const raf = requestAnimationFrame(positionMenu);
    // Reposition when the layout shifts so the menu stays glued to the
    // kebab while the user scrolls the message list or resizes.
    window.addEventListener('scroll', positionMenu, true);
    window.addEventListener('resize', positionMenu);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', positionMenu, true);
      window.removeEventListener('resize', positionMenu);
    };
  }, [kebabOpen]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (kebabOpen) {
        setKebabOpen(false);
      } else if (typeof onDismiss === 'function') {
        onDismiss();
      }
    }
  };

  const triggerEdit = () => {
    setKebabOpen(false);
    if (typeof onEdit === 'function') onEdit();
  };

  const triggerCopyLink = () => {
    setKebabOpen(false);
    if (typeof onCopyLink === 'function') onCopyLink();
  };

  // Slack parity — copy the raw message body to clipboard. Pure client
  // side; no mutation. Hidden on deleted rows (parent gates via messageText
  // being empty). The transient "Copied" toast lives in the same lane as
  // the Copy link toast so we don't introduce a second surface.
  const triggerCopyMessage = () => {
    setKebabOpen(false);
    if (!messageText) return;
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;
    if (clipboard && typeof clipboard.writeText === 'function') {
      clipboard.writeText(messageText).then(
        () => setMessageCopiedAt(Date.now()),
        () => { /* swallow — copy is best-effort */ }
      );
    }
  };

  // Delete dispatches to the parent, which opens the Slack-parity
  // confirmation modal (DeleteMessageModal). The toolbar itself does not
  // gate the action with window.confirm anymore — the modal owns the
  // confirm/cancel UX, message preview, and error surface.
  const triggerDelete = () => {
    setKebabOpen(false);
    if (typeof onDelete === 'function') onDelete();
  };

  const triggerForward = () => {
    setKebabOpen(false);
    if (typeof onForward === 'function') onForward();
  };

  const triggerPin = () => {
    setKebabOpen(false);
    if (isPinned) {
      if (typeof onUnpin === 'function') onUnpin();
    } else if (typeof onPin === 'function') {
      onPin();
    }
  };

  const triggerBookmark = () => {
    setKebabOpen(false);
    if (isBookmarked) {
      if (typeof onUnbookmark === 'function') onUnbookmark();
    } else if (typeof onBookmark === 'function') {
      onBookmark();
    }
  };

  return (
    <div
      ref={toolbarRef}
      className="message-hover-toolbar"
      role="toolbar"
      aria-label="Message actions"
      onKeyDown={handleKeyDown}
    >
      {chips.map(({ token, emoji, label }) => (
        <button
          key={token}
          type="button"
          className="message-hover-toolbar-chip"
          aria-label={`React with ${label}`}
          title={`React with ${label}`}
          onClick={() => onQuickReact && onQuickReact(token)}
        >
          <span aria-hidden="true">{emoji}</span>
        </button>
      ))}

      <span className="message-hover-toolbar-divider" aria-hidden="true" />

      <button
        type="button"
        className="message-hover-toolbar-btn"
        aria-label="Find another reaction"
        title="Find another reaction"
        onClick={() => onOpenPicker && onOpenPicker()}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      </button>

      {onOpenThread && (
        <button
          type="button"
          className="message-hover-toolbar-btn"
          aria-label="Reply in thread"
          title="Reply in thread"
          onClick={() => onOpenThread()}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {canEdit && (
        <button
          type="button"
          className="message-hover-toolbar-btn"
          aria-label="Edit message"
          title="Edit message"
          onClick={triggerEdit}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
      )}

      {canForward && (
        <button
          type="button"
          className="message-hover-toolbar-btn"
          aria-label="Forward message"
          title="Forward message"
          onClick={triggerForward}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 17 20 12 15 7" />
            <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
          </svg>
        </button>
      )}

      {canBookmark && (
        <button
          type="button"
          className={`message-hover-toolbar-btn${isBookmarked ? ' message-hover-toolbar-btn--active' : ''}`}
          aria-label={isBookmarked ? 'Remove bookmark' : 'Save for later'}
          title={isBookmarked ? 'Remove bookmark' : 'Save for later'}
          aria-pressed={isBookmarked}
          onClick={triggerBookmark}
        >
          {isBookmarked ? (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
              <path d="M5 4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v18l-7-4-7 4V4z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v18l-7-4-7 4V4z" />
            </svg>
          )}
        </button>
      )}

      <span className="message-hover-toolbar-divider" aria-hidden="true" />

      <div className="message-hover-toolbar-kebab-wrap">
        <button
          ref={kebabRef}
          type="button"
          className="message-hover-toolbar-btn"
          aria-haspopup="menu"
          aria-expanded={kebabOpen}
          aria-label="More actions"
          title="More actions"
          onClick={() => setKebabOpen((prev) => !prev)}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="19" r="1" />
          </svg>
        </button>
        {kebabOpen && (
          <div
            ref={menuRef}
            className={`message-hover-toolbar-menu message-hover-toolbar-menu--${menuDirection}`}
            role="menu"
            aria-label="More actions"
            style={menuStyle}
          >
            {canEdit && (
              <button
                type="button"
                role="menuitem"
                className="message-hover-toolbar-menu-item"
                onClick={triggerEdit}
              >
                <span className="message-hover-toolbar-menu-item-label">Edit message</span>
                <span className="message-hover-toolbar-menu-item-shortcut" aria-hidden="true">E</span>
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className="message-hover-toolbar-menu-item"
              onClick={triggerCopyLink}
            >
              <span className="message-hover-toolbar-menu-item-label">Copy link</span>
              <span className="message-hover-toolbar-menu-item-shortcut" aria-hidden="true">L</span>
            </button>
            {messageText && (
              <button
                type="button"
                role="menuitem"
                className="message-hover-toolbar-menu-item"
                onClick={triggerCopyMessage}
              >
                <span className="message-hover-toolbar-menu-item-label">Copy message</span>
                <span className="message-hover-toolbar-menu-item-shortcut" aria-hidden="true">⌘C</span>
              </button>
            )}
            {canPin && (
              <button
                type="button"
                role="menuitem"
                className="message-hover-toolbar-menu-item"
                onClick={triggerPin}
              >
                <span className="message-hover-toolbar-menu-item-label">
                  {isPinned ? 'Unpin from conversation' : 'Pin to conversation'}
                </span>
                <span className="message-hover-toolbar-menu-item-shortcut" aria-hidden="true">P</span>
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                role="menuitem"
                className="message-hover-toolbar-menu-item message-hover-toolbar-menu-item--destructive"
                onClick={triggerDelete}
              >
                <span className="message-hover-toolbar-menu-item-label">Delete message…</span>
                <span className="message-hover-toolbar-menu-item-shortcut" aria-hidden="true">delete</span>
              </button>
            )}
          </div>
        )}
        {messageCopiedAt > 0 && Date.now() - messageCopiedAt < 1800 && (
          <span className="message-hover-toolbar-copied-toast" role="status" aria-live="polite">
            Message copied
          </span>
        )}
      </div>
    </div>
  );
}

export default MessageHoverToolbar;
